"use client";

// Save (signed-in guests; listed in My Trips → Saved places) and Share (WhatsApp, copy link, or the phone's own share
// sheet) for a business page. Counts come from the server and show only from five (docs/MARKETPLACE_TRUST_2026-11-04.md).
import { useEffect, useRef, useState } from "react";
import { Check, Copy, Eye, Heart, MessageCircle, Share2 } from "lucide-react";
import { getAccessToken, getAuthHeaders } from "@/lib/auth";

function isGuest(token: string | null) {
  try {
    const part = (token || "").split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return String(JSON.parse(window.atob(part.padEnd(Math.ceil(part.length / 4) * 4, "="))).role || "").toLowerCase() === "guest";
  } catch { return false; }
}

const button = "inline-flex min-h-11 items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-800 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 disabled:opacity-50";

// 1,240 → "1,240"; 35,400 → "35K", as on social apps.
const compact = (n: number) => (n >= 10_000 ? `${new Intl.NumberFormat("en-GB", { notation: "compact", maximumFractionDigits: 1 }).format(n)}` : n.toLocaleString("en-GB"));

export default function BusinessSaveShare({ partnerId, name, saves, shares, views }: { partnerId: number; name: string; saves: number | null; shares: number | null; views?: number | null }) {
  const [signedIn, setSignedIn] = useState(false);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [menu, setMenu] = useState(false);
  const [copied, setCopied] = useState(false);
  const [notice, setNotice] = useState("");
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isGuest(getAccessToken())) return;
    setSignedIn(true);
    // Only signed-in guests ask, once per page view.
    fetch("/api/v1/guest/saved", { headers: getAuthHeaders(), cache: "no-store" })
      .then((response) => response.json())
      .then((payload) => { if (payload.success) setSaved(payload.data.some((row: { partner_id: number }) => row.partner_id === partnerId)); })
      .catch(() => undefined);
  }, [partnerId]);

  useEffect(() => {
    if (!menu) return;
    const close = (event: MouseEvent) => { if (!menuRef.current?.contains(event.target as Node)) setMenu(false); };
    const escape = (event: KeyboardEvent) => { if (event.key === "Escape") setMenu(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", escape);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", escape); };
  }, [menu]);

  async function toggleSave() {
    if (!signedIn) {
      window.location.assign(`/public/guest-login?next=${encodeURIComponent(window.location.pathname)}`);
      return;
    }
    setBusy(true);
    setNotice("");
    try {
      const response = await fetch("/api/v1/guest/saved", { method: "POST", headers: getAuthHeaders({ "Content-Type": "application/json" }), body: JSON.stringify({ partner_id: partnerId, saved: !saved }) });
      const payload = await response.json();
      if (!response.ok || !payload.success) throw new Error(payload.error || "Could not save.");
      setSaved(payload.saved);
      setNotice(payload.saved ? "Saved to My Trips." : "Removed from My Trips.");
    } catch (problem) {
      setNotice(problem instanceof Error ? problem.message : "Could not save.");
    } finally { setBusy(false); }
  }

  function record(channel: "whatsapp" | "copy" | "native") {
    void fetch("/api/v1/public/business-share", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ partner_id: partnerId, channel }), keepalive: true }).catch(() => undefined);
  }

  async function share() {
    const url = window.location.href.split("?")[0];
    if (typeof navigator.share === "function" && window.matchMedia("(pointer: coarse)").matches) {
      try { await navigator.share({ title: name, url }); record("native"); } catch { /* closed the sheet */ }
      return;
    }
    setMenu((open) => !open);
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(window.location.href.split("?")[0]);
      setCopied(true);
      record("copy");
      window.setTimeout(() => { setCopied(false); setMenu(false); }, 1200);
    } catch { setNotice("Copy the address from your browser's address bar."); }
  }

  const url = typeof window === "undefined" ? "" : window.location.href.split("?")[0];
  return (
    <div className="mt-4">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={toggleSave} disabled={busy} aria-pressed={saved} aria-label={saved ? "Saved to My Trips. Remove" : "Save to My Trips"} title={saved ? "Saved to My Trips" : "Save to My Trips"}
          className={saves ? button : button.replace("px-4", "w-11 justify-center")}>
          <Heart className={`h-5 w-5 shrink-0 transition-transform ${saved ? "scale-110 fill-sky-600 text-sky-600" : "text-slate-600"}`} aria-hidden />
          {saves ? <span className="text-slate-600">{compact(saves)}</span> : null}
        </button>
        <div className="relative" ref={menuRef}>
          <button type="button" onClick={share} aria-expanded={menu} aria-haspopup="menu" className={button}>
            <Share2 className="h-4 w-4 text-slate-600" aria-hidden /> Share{shares ? <span className="text-slate-500">· {compact(shares)}</span> : null}
          </button>
          {menu ? (
            <div role="menu" className="absolute left-0 z-20 mt-2 w-56 rounded-xl border border-slate-200 bg-white p-1 shadow-lg">
              <a role="menuitem" href={`https://wa.me/?text=${encodeURIComponent(`${name} on Cybo One: ${url}`)}`} target="_blank" rel="noopener noreferrer"
                onClick={() => { record("whatsapp"); setMenu(false); }} className="flex min-h-11 items-center gap-2 rounded-lg px-3 text-sm font-semibold text-slate-800 hover:bg-slate-100">
                <MessageCircle className="h-4 w-4 text-slate-600" aria-hidden /> Share on WhatsApp
              </a>
              <button role="menuitem" type="button" onClick={copy} className="flex min-h-11 w-full items-center gap-2 rounded-lg px-3 text-left text-sm font-semibold text-slate-800 hover:bg-slate-100">
                {copied ? <Check className="h-4 w-4 text-sky-700" aria-hidden /> : <Copy className="h-4 w-4 text-slate-600" aria-hidden />} {copied ? "Link copied" : "Copy link"}
              </button>
            </div>
          ) : null}
        </div>
        {views ? <span className="inline-flex min-h-11 items-center gap-1.5 px-2 text-sm text-slate-600" title="Visitors to this business on Cybo One, counted once a day"><Eye className="h-4 w-4" aria-hidden />{compact(views)} views</span> : null}
      </div>
      <p className="mt-1 min-h-5 text-xs text-slate-500" aria-live="polite">{notice}</p>
    </div>
  );
}
